import { useState, useEffect, useCallback } from "react";
import { api } from "@/services/api";
import PropertyModal from "./components/PropertyModal";

interface Property {
  id: number;
  title: string;
  type: string;
  purpose: string;
  status: string;
  price: string;
  currency: string;
  city: string;
  rm_name: string;
  is_featured: number;
  primary_image: string | null;
  created_at: string;
}

interface ListResponse {
  data: Property[];
  total: number;
  page: number;
  limit: number;
}

const TYPE_OPTIONS = ["villa", "cottage", "duplex", "commercial"];
const REGION_OPTIONS = ["All", "South India", "North India", "West India", "East India", "Central India", "Northeast India"];
const STATUS_OPTIONS = ["available", "sold", "rented", "under-construction", "off-market"];
const PURPOSE_OPTIONS = ["sale", "rent", "investment"];

const STATUS_BADGE: Record<string, string> = {
  available: "bg-primary-600 text-background-50",
  sold: "bg-accent-500 text-background-50",
  rented: "bg-secondary-500 text-background-50",
  "under-construction": "bg-accent-100 text-accent-900",
  "off-market": "bg-background-200 text-foreground-700",
};

export default function AdminProperties() {
  const [data, setData] = useState<ListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState<{ type: string; status: string; purpose: string; city: string; region: string }>({
    type: "", status: "", purpose: "", city: "", region: "",
  });
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<number[]>([]);
  const [modal, setModal] = useState<null | { mode: "create" } | { mode: "edit"; id: number }>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
    params.set("page", String(page));
    params.set("limit", "15");
    try {
      const res = await api.get<ListResponse>(`/properties?${params.toString()}`);
      setData(res);
    } catch (e: any) {
      setError(e?.message || "Failed to load projects");
    } finally {
      setLoading(false);
    }
  }, [search, filters, page]);

  useEffect(() => { load(); }, [load]);

  function toggleSelect(id: number) {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }

  async function deleteProperty(id: number) {
    if (!window.confirm("Delete this project permanently?")) return;
    try {
      await api.del(`/properties/${id}`);
      setSelected((prev) => prev.filter((x) => x !== id));
      load();
    } catch (e: any) {
      alert(e?.message || "Delete failed");
    }
  }

  const pageCount = data ? Math.max(Math.ceil(data.total / data.limit), 1) : 1;

  return (
    <div className="space-y-5">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <div className="flex items-center gap-2 rounded-full border border-background-300 bg-background-50 px-4 py-2 w-full sm:w-72">
          <i className="ri-search-line text-foreground-500"></i>
          <input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search projects..."
            className="flex-1 bg-transparent text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none"
          />
        </div>
        <div className="flex items-center gap-2">
          {selected.length > 0 && (
            <span className="text-sm text-foreground-600">{selected.length} selected</span>
          )}
          <button
            onClick={() => setModal({ mode: "create" })}
            className="inline-flex items-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full bg-primary-600 text-background-50 px-5 py-2.5 hover:bg-primary-700"
          >
            <i className="ri-add-line"></i> Add Project
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <select
          value={filters.type}
          onChange={(e) => { setFilters((f) => ({ ...f, type: e.target.value })); setPage(1); }}
          className="rounded-full border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
        >
          <option value="">All Types</option>
          {TYPE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select
          value={filters.status}
          onChange={(e) => { setFilters((f) => ({ ...f, status: e.target.value })); setPage(1); }}
          className="rounded-full border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
        >
          <option value="">All Status</option>
          {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select
          value={filters.purpose}
          onChange={(e) => { setFilters((f) => ({ ...f, purpose: e.target.value })); setPage(1); }}
          className="rounded-full border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
        >
          <option value="">All Purposes</option>
          {PURPOSE_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <select
          value={filters.region}
          onChange={(e) => { setFilters((f) => ({ ...f, region: e.target.value })); setPage(1); }}
          className="rounded-full border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
        >
          <option value="">All Regions</option>
          {REGION_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        {(Object.values(filters).some(Boolean) || search) && (
          <button
            onClick={() => { setFilters({ type: "", status: "", purpose: "", city: "", region: "" }); setSearch(""); setPage(1); }}
            className="inline-flex items-center gap-1 text-sm text-foreground-600 hover:text-primary-700"
          >
            <i className="ri-close-circle-line"></i> Clear
          </button>
        )}
      </div>

      {error && (
        <div className="rounded-2xl bg-accent-100 border border-accent-300 p-4 text-sm text-accent-900">{error}</div>
      )}

      {/* Table */}
      <div className="rounded-2xl bg-background-50 border border-background-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-background-100 text-left text-xs uppercase tracking-wider text-foreground-600">
                <th className="px-4 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={data ? selected.length === data.data.length && data.data.length > 0 : false}
                    onChange={(e) => {
                      if (!data) return;
                      setSelected(e.target.checked ? data.data.map((p) => p.id) : []);
                    }}
                  />
                </th>
                <th className="px-4 py-3">Project</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Price</th>
                <th className="px-4 py-3">RM</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-100">
              {loading && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-foreground-600">
                  <i className="ri-loader-4-line animate-spin"></i> Loading...
                </td></tr>
              )}
              {!loading && data?.data.map((p) => (
                <tr key={p.id} className="hover:bg-background-100/60">
                  <td className="px-4 py-3">
                    <input type="checkbox" checked={selected.includes(p.id)} onChange={() => toggleSelect(p.id)} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary-100 text-primary-800 shrink-0">
                        <i className="ri-building-2-line"></i>
                      </span>
                      <div className="min-w-0">
                        <p className="font-semibold text-foreground-950 truncate max-w-[220px]">{p.title}</p>
                        <p className="text-xs text-foreground-600">{p.city || "—"}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 capitalize text-foreground-800">{p.type}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold capitalize ${STATUS_BADGE[p.status] || "bg-background-200 text-foreground-700"}`}>
                      {p.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-semibold text-foreground-950">
                    {p.currency} {Number(p.price || 0).toLocaleString("en-IN")}
                  </td>
                  <td className="px-4 py-3 text-foreground-700">{p.rm_name || "—"}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => setModal({ mode: "edit", id: p.id })}
                        className="flex items-center justify-center w-8 h-8 rounded-lg text-foreground-600 hover:bg-background-100"
                        aria-label="Edit"
                      >
                        <i className="ri-pencil-line"></i>
                      </button>
                      <button
                        onClick={() => deleteProperty(p.id)}
                        className="flex items-center justify-center w-8 h-8 rounded-lg text-accent-700 hover:bg-accent-100"
                        aria-label="Delete"
                      >
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && data?.data.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-foreground-600">
                  No projects found.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-background-100">
          <p className="text-xs text-foreground-600">
            Showing {data ? Math.min((page - 1) * data.limit + 1, data.total) : 0}–
            {data ? Math.min(page * data.limit, data.total) : 0} of {data?.total ?? 0}
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(p - 1, 1))}
              disabled={page <= 1}
              className="flex items-center justify-center w-8 h-8 rounded-lg text-foreground-600 hover:bg-background-100 disabled:opacity-40"
            >
              <i className="ri-arrow-left-s-line"></i>
            </button>
            <span className="px-3 text-sm text-foreground-700">{page} / {pageCount}</span>
            <button
              onClick={() => setPage((p) => Math.min(p + 1, pageCount))}
              disabled={page >= pageCount}
              className="flex items-center justify-center w-8 h-8 rounded-lg text-foreground-600 hover:bg-background-100 disabled:opacity-40"
            >
              <i className="ri-arrow-right-s-line"></i>
            </button>
          </div>
        </div>
      </div>

      {modal && (
        <PropertyModal
          mode={modal.mode}
          id={modal.mode === "edit" ? modal.id : undefined}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
}